import moment from "moment";

import { NoticeBoardSearchSettings } from "@/Appointments/Models/Settings/NoticeBoardSearchSettings";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { QuestionnairePopupController } from "@/Common/Components/Questionnaire/QuestionnairePopupController";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { QuestionnaireNotice } from "@/Common/Models/Questionnaires/QuestionnaireNotice";
import { QuestionnaireNoticeTableItem } from "@/Common/Models/Questionnaires/QuestionnaireNoticeTableItem";
import { QuestionnaireResult } from "@/Common/Models/Questionnaires/QuestionnaireResult";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { NoticeBoardAction } from "./NoticeBoardAction";

interface QuestionnaireNoticeBoardTableProps {
    shouldReloadQuestionnaireBoard: boolean;
}

class QuestionnaireNoticeBoardTableState {
    public questionnaireNotices: QuestionnaireNotice[];

    public selectedQuestionnaire: QuestionnaireStatusInfo;

    // settings properties
    public searchText: string;
    public showSetting: boolean;

    public isAllSitesChecked: boolean;
    public allSites: SiteUIModel[];
    public isOnlySelectedSiteChecked: boolean;

    public isAllEmployeesChecked: boolean;
    public employeeSearchText: string;
    public allEmployees: EmployeeUIModel[];

    public isAllResultsChecked: boolean;
    public allResults: CheckboxUIModel[];

    public isAllPatientContactedChecked: boolean;
    public allPatientContacted: CheckboxUIModel[];

    public currentPage: number;
    public pageNumber: number;
    public pageSize: number;
    public currentPageSize: number;
    public totalRows: number;

    public isSaveSettingsChecked: boolean;
}

class QuestionnaireNoticeBoardTable extends BaseReactComponent<QuestionnaireNoticeBoardTableProps, QuestionnaireNoticeBoardTableState> {
    private readonly componentId = "QuestionnaireNoticeBoardTable";
    private action: NoticeBoardAction;
    private searchTextTimeout: any;

    public constructor(props: QuestionnaireNoticeBoardTableProps) {
        super(props);
        this.action = new NoticeBoardAction(err => this.handleError(err));
    }

    public componentDidMount(): void {
        const settings = cacheStore.UserSettings?.NoticeBoardSearchSettings;

        this.mutateState(
            s => {
                s.isAllSitesChecked = settings && settings.IsAllSitesChecked;
                s.allSites = this.loadAllSites().mutate(x => x.Checked = false, x => settings && !(settings.SiteIds.indexOf(x.Id) > -1));
                s.isOnlySelectedSiteChecked = settings && settings.IsOnlySelectedSiteChecked;

                s.isAllEmployeesChecked = settings && settings.IsAllEmployeesChecked;
                s.allEmployees = this.loadAllEmployees().mutate(x => x.Checked = true, x => settings && settings.EmployeeContactFacetIds.indexOf(x.ContactFacetId) > -1);

                s.isAllResultsChecked = settings && settings.IsAllStatusesChecked;
                s.allResults = settings
                    ? this.loadAllResults().mutate(x => x.Checked = true, x => settings.Status.indexOf(x.Id) > -1)
                    : this.loadAllResults().mutate(x => x.Checked = true, x => Number(x.Id) !== QuestionnaireResult.NegativeScreening);

                s.isAllPatientContactedChecked = settings && settings.IsAllPatientContactedChecked;
                s.allPatientContacted = this.loadAllPatientContactedOptions().mutate(x => x.Checked = true, x => settings && settings.PatientContacted.indexOf(x.Id) > -1);

                s.pageSize = settings ? settings.PageSize : this.pageSizeOptions[0];
                s.currentPageSize = s.pageSize;
                s.pageNumber = 1;
                s.currentPage = 1;
                s.totalRows = 0;

                s.isSaveSettingsChecked = false;

                s.questionnaireNotices = [];
            },
            () => this.loadQuestionnaireNotices()
        );
    }

    public componentDidUpdate(prevProps: Readonly<QuestionnaireNoticeBoardTableProps>): void {
        if (this.props !== prevProps) {
            if (prevProps.shouldReloadQuestionnaireBoard != this.props.shouldReloadQuestionnaireBoard && this.props.shouldReloadQuestionnaireBoard) {
                this.loadQuestionnaireNotices();
            }
        }
    }

    public render(): JSX.Element {
        if (!this.state)
            return null;

        return (
            <div className="myle-table">
                {this.renderHeader()}
                {this.renderTable()}
                {this.renderFooter()}
                {this.state.showSetting && this.renderSetting()}

                {this.state.selectedQuestionnaire && (
                    <QuestionnairePopupController
                        questionnaire={this.state.selectedQuestionnaire}
                        onClose={() => this.mutateState(s => s.selectedQuestionnaire = null)}
                        onSubmitted={() => this.mutateState(s => s.selectedQuestionnaire = null, () => this.loadQuestionnaireNotices())}
                    />
                )}
            </div>
        );
    }

    private renderHeader(): JSX.Element {
        return (
            <div className="header">
                <div className="row">
                    <div className="col-sm-6 hidden-sm-down">
                        <h3 className="thin-font">{cacheStore.GetString("QuestionnairesLabel")}</h3>
                    </div>
                    <div className="col-sm-6">
                        <div className="pull-right">
                            <input
                                type="text"
                                value={this.state.searchText}
                                placeholder={cacheStore.GetString("SearchLabel")}
                                className="form-control primary-search"
                                data-cy="questionnaireNoticeBoardTable-search-input"
                                onChange={(e: any) => this.handleOnSearchTextChanged(e.target.value)}
                            />
                        </div>
                        <div className="pull-right">
                            <RefreshButton rightMargin={5} onClick={() => this.loadQuestionnaireNotices(() => sessionStore.dispatch(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts))} />
                            <SettingsButton rightMargin={13} onClick={() => this.mutateState(s => s.showSetting = true)} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderTable(): JSX.Element {
        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this.componentId, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        const cssClass = "notice-board-table" + (this.hasFooter() ? "" : "-no-footer");

        return (
            <MyleTable
                TableRenderOptions={tableRenderOptions}
                TableId={this.componentId}
                TableDataCy="questionnaireNoticeBoardTable-table"
                Columns={tableColumns}
                DataSource={Immutable.List<QuestionnaireNoticeTableItem>(this.state.questionnaireNotices.map(x => new QuestionnaireNoticeTableItem(x)))}
                RowClickFunction={data => this.handleOnRowClicked(data)}
                CssClass={cssClass}
                ShowHeaderTextInBold
                HideFooter
                NoScrollLimit
            />
        );
    }

    private renderFooter(): JSX.Element {
        if (!this.hasFooter())
            return null;

        return (
            <div className="ws-footer">
                <SimplePagerControl
                    PageSize={this.state.pageSize}
                    TotalRows={this.state.totalRows}
                    CurrentPage={this.state.currentPage}
                    changePageFunction={(pageNumber: number) => this.mutateState(s => s.pageNumber = pageNumber, () => this.loadQuestionnaireNotices())}
                />
            </div>
        );
    }

    private hasFooter(): boolean {
        return this.state.totalRows > this.state.pageSize;
    }

    private renderSetting(): JSX.Element {
        return (
            <div className="full-screen" onClick={() => this.handleOnSearchBySettings()}>
                <div className="filter-component" onClick={e => e.stopPropagation()} style={{ maxWidth: "950px", right: "20px", top: "200px" }}>
                    <table className="f-content">
                        <thead className="text-uppercase">
                            <tr>
                                {this.state.allSites.length > 1 && (
                                    <th className="first" style={{ width: "200px" }}>
                                        <CheckboxControl
                                            id="sites_check"
                                            label={cacheStore.GetString("SitesLabel")}
                                            onChange={isChecked => this.mutateState(s => { s.isAllSitesChecked = isChecked; s.allSites.forEach(x => x.Checked = isChecked); })}
                                            checked={this.state.isAllSitesChecked}
                                            DivWrap={true}
                                        />
                                    </th>
                                )}
                                <th className="first">
                                    <CheckboxControl
                                        id="employees_check"
                                        label={cacheStore.GetString("EmployeesLabel")}
                                        onChange={isChecked => this.mutateState(s => { s.isAllEmployeesChecked = isChecked; s.allEmployees = s.allEmployees.mutate(emp => emp.Checked = isChecked, emp => this.filteredEmployee.some(x => x.Id === emp.Id)); })}
                                        checked={this.state.isAllEmployeesChecked}
                                        DivWrap={true}
                                    />
                                </th>
                                <th className="first">
                                    <CheckboxControl
                                        id="status_check"
                                        label={cacheStore.GetString("ResultsLabel")}
                                        onChange={isChecked => this.mutateState(s => { s.isAllResultsChecked = isChecked; s.allResults.forEach(x => x.Checked = isChecked); })}
                                        checked={this.state.isAllResultsChecked}
                                        DivWrap={true}
                                    />
                                </th>
                                <th className="last">
                                    <CheckboxControl
                                        id="patient_contacted_check"
                                        label={cacheStore.GetString("StatusLabel")}
                                        onChange={isChecked => this.mutateState(s => { s.isAllPatientContactedChecked = isChecked; s.allPatientContacted.forEach(x => x.Checked = isChecked); })}
                                        checked={this.state.isAllPatientContactedChecked}
                                        DivWrap={true}
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {this.state.allSites.length > 1 && (
                                    <td className="first sites-column">
                                        <div className="checkbox-list">
                                            <CheckedListControl
                                                DataSource={Immutable.List(this.state.allSites)}
                                                onChange={(isChecked, id) => this.mutateState(s => s.allSites = s.allSites.mutate(site => site.Checked = isChecked, site => site.Id === id))}
                                                MaxTextLength={25}
                                            />

                                            <div className="text-uppercase">
                                                <CheckboxControl
                                                    id="sites_onlySelected"
                                                    label={cacheStore.GetString("ShowOnlySelectedSiteLabel")}
                                                    checked={this.state.isOnlySelectedSiteChecked}
                                                    onChange={isChecked => this.mutateState(s => s.isOnlySelectedSiteChecked = isChecked)}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                )}
                                <td className="first" style={{ width: "227px" }}>
                                    <TextboxControl
                                        id="employee_search"
                                        className="form-control"
                                        value={this.state.employeeSearchText}
                                        onChange={text => this.mutateState(s => s.employeeSearchText = text)}
                                        placeholder={cacheStore.GetString("SearchLabel")}
                                    />
                                    <div className="checkbox-list">
                                        <CheckedListControl
                                            DataSource={this.filteredEmployee}
                                            onChange={(isChecked, id) => this.mutateState(s => s.allEmployees = s.allEmployees.mutate(emp => emp.Checked = isChecked, emp => emp.Id === id))}
                                            limitTextWidth={true}
                                            sort={true}
                                        />
                                    </div>
                                </td>
                                <td className="first">
                                    <div className="checkbox-list">
                                        <CheckedListControl
                                            DataSource={Immutable.List(this.state.allResults)}
                                            onChange={(isChecked, id) => this.mutateState(s => s.allResults = s.allResults.mutate(x => x.Checked = isChecked, x => x.Id === id))}
                                            sort={false}
                                        />
                                    </div>
                                </td>

                                <td className="last">
                                    <div>
                                        <CheckedListControl
                                            DataSource={Immutable.List(this.state.allPatientContacted)}
                                            onChange={(isChecked, id) => this.mutateState(s => s.allPatientContacted = s.allPatientContacted.mutate(x => x.Checked = isChecked, x => x.Id === id))}
                                            sort={false}
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="f-footer flex-container">
                        <div style={{ alignItems: "center" }} className="flex-container">
                            <select className="form-control" style={{ width: "unset" }} onChange={(e: any) => this.handePageSizeChange(e.target.value)} value={this.state.currentPageSize.toString()}>
                                {this.pageSizeOptions.map(size =>
                                    <option key={size} value={size.toString()}>{size}</option>
                                )}
                            </select>
                            <span style={{ marginLeft: "10px" }}>{cacheStore.GetString("ResultsPerPageLabel").toUpperCase()}</span>
                        </div>
                        <div className="pull-right">
                            <span style={{ marginRight: "20px", verticalAlign: "bottom" }}>
                                <CheckboxControl
                                    id="noticeBoardSaveSettingsChecked"
                                    checked={this.state.isSaveSettingsChecked}
                                    label={cacheStore.GetString("RememberUserSettings")}
                                    onChange={() => this.mutateState(s => s.isSaveSettingsChecked = !s.isSaveSettingsChecked)}
                                />
                            </span>
                            <button className="btn btn-default push-right-5" onClick={() => this.closeSettingsContainer(true)}>{cacheStore.GetString("CancelLabel")}</button>
                            <button className="btn btn-primary" onClick={() => this.handleOnSearchBySettings()}>{cacheStore.GetString("Ok_Label")}</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handePageSizeChange(value: any): void {
        if (isNaN(value))
            return;

        this.mutateState(s => s.currentPageSize = Number(value));
    }

    private get filteredEmployee(): Immutable.List<EmployeeUIModel> {
        let checkedSiteList = this.state.allSites.filter(s => s.Checked).map(s => s.Id);
        let employees = SearchMenuHelper.FilterCliniciansMembersOfSites(Immutable.List(this.state.allEmployees), checkedSiteList);
        var filterString = this.state.employeeSearchText?.trim() || "";
        if (filterString !== "") {
            employees = SearchMenuHelper.FilterCliniciansByName(filterString, employees);
        }
        return employees;
    }

    private get pageSizeOptions(): number[] {
        return cacheStore.getDefaultPageSizes();
    }

    private handleOnSearchTextChanged(text: string): void {
        this.mutateState(
            s => s.searchText = text,
            () => {
                clearTimeout(this.searchTextTimeout);
                this.searchTextTimeout = window.setTimeout(() => this.loadQuestionnaireNotices(), 500);
            }
        );
    }

    private handleOnRowClicked(data: QuestionnaireNoticeTableItem): void {
        if (!data?.Model)
            return;

        sessionStore.dispatch(
            LocalOperations.Calendar.Appointment_Show_Edit_Dialog,
            {
                isPredefined: false,
                appointmentId: data.Model.AppointmentId
            }
        );
    }

    private handleOnSearchBySettings(): void {
        const userSettings = new UserSettings();
        userSettings.NoticeBoardSearchSettings = NoticeBoardSearchSettings.fromModel(this.state);
        cacheStore.UserSettings.NoticeBoardSearchSettings = userSettings.NoticeBoardSearchSettings;

        this.mutateState(s => s.pageNumber = 1, () => {
            if (!this.state.isSaveSettingsChecked) {
                this.loadQuestionnaireNotices();
                this.closeSettingsContainer();

                return;
            }

            this.action
                .saveSearchSettings(userSettings)
                .then(() => this.loadQuestionnaireNotices());

            this.closeSettingsContainer();
        });
    }

    private handleOnPatientContactedChecked(isChecked: boolean, questionnaireNotice: QuestionnaireNotice): void {
        const contactedDate = isChecked ? moment().toDate() : null;
        this.action
            .updateQuestionnairePatientContacted(questionnaireNotice.QuestionnaireInstanceId, contactedDate)
            .then(() => this.loadQuestionnaireNotices());
    }

    private loadAllSites(): SiteUIModel[] {
        return cacheStore.SiteCache?.map(s => {
            let site = SiteUIModel.FromCompositeObject(s);
            site.Checked = true;
            site.Model = s.Model;
            return site;
        }) || [];
    }

    private loadAllEmployees(): EmployeeUIModel[] {
        return cacheStore.GetActiveEmployees()?.map(x => EmployeeUIModel.fromCompositeObject(x)) || [];
    }

    private loadAllResults(): CheckboxUIModel[] {
        let statuses = [];
        for (let key in QuestionnaireResult) {
            let value = Number(key);
            if (!isNaN(value)) {
                let status = new CheckboxUIModel();
                status.Id = `${value}`;
                status.Name = cacheStore.GetString(`${QuestionnaireResult[key]}Label`);

                statuses.push(status);
            }
        }
        return statuses.sortByProperty(x => x.Id);
    }

    private get notContacted(): string {
        return "not_contacted";
    }

    private get contacted(): string {
        return "contacted";
    }

    private loadAllPatientContactedOptions(): CheckboxUIModel[] {
        let notContacted = CheckboxUIModel.New(this.notContacted, cacheStore.GetString("NotContactedLabel"), false);
        let contacted = CheckboxUIModel.New(this.contacted, cacheStore.GetString("ContactedLabel"), false);
        return [notContacted, contacted];
    }

    private loadQuestionnaireNotices(refreshCount: () => void = null): void {
        let onlySelectedSiteIds = this.state.isOnlySelectedSiteChecked
            ? this.state.allSites.filter(x => x.Checked).map(x => x.Id)
            : [];

        let employeeIds = this.state.allEmployees
            .filter(emp => emp.Checked)
            .map(x => x.ContactFacetId);

        let results = this.state.allResults
            .filter(s => s.Checked)
            .map(s => Number(s.Id) as QuestionnaireResult);

        let checkedPatientContacted = this.state.allPatientContacted.filter(x => x.Checked);
        let onlyContactedPatient = (checkedPatientContacted.length === 0 || checkedPatientContacted.length === this.loadAllPatientContactedOptions().length)
            ? null
            : checkedPatientContacted.some(x => x.Id === this.contacted);

        this.action
            .loadQuestionnairesForNoticeBoard(this.state.searchText, onlySelectedSiteIds, employeeIds, results, onlyContactedPatient, this.state.currentPageSize === this.state.pageSize ? this.state.pageNumber : 1, this.state.currentPageSize)
            .then(response => this.mutateState(
                s => {
                    s.questionnaireNotices = response.Results.map(x => new QuestionnaireNotice(x));
                    s.currentPage = response.CurrentPage;
                    s.totalRows = response.TotalQueryRows;
                    s.pageSize = s.currentPageSize;
                },
                () => refreshCount?.()
            ));
    }

    private handleError(errors: ErrorMessage[]): void {
        if (errors?.length)
            return;

        sessionStore.showPopup(errors);
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<QuestionnaireNoticeTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Time":
                        return {
                            renderCell: item => <div>{item.Model?.AppointmentStartDate && moment(item.Model.AppointmentStartDate).format("HH:mm")}</div>
                        };

                    case "Patient":
                        return {
                            renderCell: item => this.renderPatientColumn(item.Model)
                        };

                    case "HealthCard":
                        return {
                            renderCell: item => <div>{item.Model?.PatientNAM}</div>
                        };

                    case "Provider":
                        return {
                            renderCell: item => {
                                if (!item.Model?.ProviderContactFacetId)
                                    return <div />;

                                let clinician = cacheStore.GetCliniciansPersonsByContactFacetId(item.Model.ProviderContactFacetId);
                                return <div>{clinician.NameWithPrefix}</div>;
                            }
                        };

                    case "PhoneNumber":
                        return {
                            renderCell: item => this.renderPhoneColumn(item.Model)
                        };

                    case "Questionnaire":
                        return {
                            renderCell: item => this.renderQuestionnaireColumn(item.Model)
                        };

                    case "PatientContacted":
                        return {
                            renderCell: item => (
                                <div className="flex-container" style={{ justifyContent: "center" }}>
                                    <CheckboxControl
                                        id={item.Model?.PatientContactId}
                                        checked={!!item.Model?.PatientContactedDate}
                                        onChange={isChecked => this.handleOnPatientContactedChecked(isChecked, item.Model)}
                                    />
                                </div>
                            )
                        };
                }
            },
            false
        );
    }

    private renderPatientColumn(questionnaireNotice: QuestionnaireNotice): JSX.Element {
        if (!questionnaireNotice)
            return <div />;

        if (!questionnaireNotice.HasPatientProfile)
            return <strong style={{ color: "#e71e1c" }}>{questionnaireNotice.AppointmentPatientNameStr}</strong>;

        return (
            <div key="patientLink">
                <HyperLinkRedirectControl
                    target={RedirectTo.Patient}
                    redirectValue={questionnaireNotice.PatientContactId}
                    text={NameHelper.formatFullNameForHasPatientName(questionnaireNotice)}
                />
            </div>
        );
    }

    private renderPhoneColumn(questionnaireNotice: QuestionnaireNotice): JSX.Element {
        if (!questionnaireNotice || !questionnaireNotice.PatientPhoneNumbers?.any())
            return <div />;

        var phones = questionnaireNotice.PatientPhoneNumbers
            .map(x => new PhoneNumber(x).format("({1}) {2} {3}"))
            .join(", ");
        return <div>{phones}</div>;
    }

    private renderQuestionnaireColumn(questionnaireNotice: QuestionnaireNotice): JSX.Element {
        if (!questionnaireNotice)
            return <div />;

        return (
            <div>
                {questionnaireNotice.Questionnaire.renderLink(
                    q => this.mutateState(s => s.selectedQuestionnaire = q),
                    questionnaireNotice.AppointmentStartDate,
                    questionnaireNotice.AppointmentStatusLookupId,
                    questionnaireNotice.PatientContactId,
                    !questionnaireNotice.HasPatientProfile
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TimeLabel"),
                FieldName: "Time",
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "Patient",
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("HealthCard"),
                FieldName: "HealthCard",
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ProviderLabel"),
                FieldName: "Provider",
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PhoneNumberLabel"),
                FieldName: "PhoneNumber",
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("QuestionnaireLabel"),
                FieldName: "Questionnaire",
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientContactedLabel"),
                FieldName: "PatientContacted",
                WidthPercentageDefault: 10
            })
        ];
    }

    private closeSettingsContainer(isCancel: boolean = false): void {
        this.mutateState(s => {
            s.showSetting = false;
            s.isSaveSettingsChecked = false;
            if (isCancel)
                s.currentPageSize = s.pageSize;
        });
    }
}

export { QuestionnaireNoticeBoardTable, QuestionnaireNoticeBoardTableState };
